import { ReactNode, useState } from 'react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MessageSquare,
  Loader2,
  Trash2,
  RotateCcw,
  Eraser,
  Skull,
  Flag,
  MessagesSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  formatEpoch,
  inquiryVisitorName,
  isInquiryMessage,
  messageEntity,
  STATUS_BADGE,
  threadParamsFromMessage,
} from '@/client/types/messaging';
import { useMessageDetailQuery } from '@/client/api/messaging/messaging.query';
import {
  useFlagReportMutation,
  usePurgeMessageMutation,
  useRedactMessageMutation,
  useRestoreMessageMutation,
  useSoftDeleteMessageMutation,
} from '@/client/api/messaging/messaging.mutation';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import {
  MessageParties,
  OrgPartyLink,
  UserPartyLink,
} from '@/client/components/admin/Messaging/PartyHoverCard';

interface MessageDetailsDrawerProps {
  msgRef: string | null;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  canModerate: boolean;
  canDelete: boolean;
  onClose: () => void;
  onViewThread?: (params: NonNullable<ReturnType<typeof threadParamsFromMessage>>) => void;
}

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="mb-0.5 text-xs text-gray-500">{label}</p>
    <div className="break-all text-sm font-medium text-gray-900">{value ?? '—'}</div>
  </div>
);

const FlagChip = ({ label, on }: { label: string; on?: boolean }) => (
  <span
    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
      on ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'
    }`}
  >
    {label}: {on ? 'yes' : 'no'}
  </span>
);

const MessageDetailsDrawer = ({
  msgRef,
  isOpen,
  setIsOpen,
  canModerate,
  canDelete,
  onClose,
  onViewThread,
}: MessageDetailsDrawerProps) => {
  const [reason, setReason] = useState('');
  const { data: msg, isLoading } = useMessageDetailQuery(isOpen ? msgRef : null);
  const softDelete = useSoftDeleteMessageMutation();
  const restore = useRestoreMessageMutation();
  const redact = useRedactMessageMutation();
  const purge = usePurgeMessageMutation();
  const flag = useFlagReportMutation();

  if (!isOpen) return null;

  const ref = msg?.id || msg?.hash || msgRef || '';
  const inquiry = isInquiryMessage(msg);
  const threadParams = msg ? threadParamsFromMessage(msg) : null;
  const busy =
    softDelete.isPending ||
    restore.isPending ||
    redact.isPending ||
    purge.isPending ||
    flag.isPending;

  const run = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      toast.success(label, { richColors: true });
      setReason('');
    } catch (error: any) {
      toast.error(error?.message || `Failed: ${label}`, { richColors: true });
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className="mx-auto flex max-h-[92vh] max-w-2xl flex-col">
        <DrawerHeader className="border-b border-gray-100 text-left">
          <DrawerTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            {isLoading ? 'Loading…' : `Message ${msg?.id?.slice(0, 8) ?? msgRef?.slice(0, 8) ?? ''}…`}
          </DrawerTitle>
          <DrawerDescription>
            {msg ? <MessageParties msg={msg} /> : 'Message detail & moderation'}
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}
          {!isLoading && msg && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold uppercase ${
                    STATUS_BADGE[msg.status ?? 'sent'] ?? STATUS_BADGE.sent
                  }`}
                >
                  {msg.status ?? 'sent'}
                </span>
                {inquiry && (
                  <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
                    Inquiry (display-only)
                  </span>
                )}
              </div>

              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs text-gray-500">Body</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                  {msg.message || (msg.redacted ? '[removed by moderator]' : '—')}
                </p>
                {msg.attachment?.url && (
                  <a
                    href={String(msg.attachment.url)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-xs text-blue-600 hover:underline"
                  >
                    Attachment
                  </a>
                )}
              </div>

              {inquiry && (
                <div className="rounded-lg border border-amber-100 bg-amber-50 p-4 text-sm text-amber-950">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-800">
                    Off-platform visitor
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <InfoRow label="Name" value={inquiryVisitorName(msg) ?? '—'} />
                    <InfoRow label="Email" value={msg.attachment?.visitor_email ?? '—'} />
                    <InfoRow label="Phone" value={msg.attachment?.visitor_phone ?? '—'} />
                    <InfoRow label="Listing eid" value={msg.attachment?.eid ?? '—'} />
                  </div>
                  <p className="mt-2 text-xs text-amber-800">
                    Not a FeelyFeely user — reply by email or phone above, not in-thread.
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <FlagChip label="read" on={!!msg.is_read} />
                <FlagChip label="delivered" on={!!msg.is_delivered} />
                <FlagChip label="deleted" on={!!msg.is_deleted} />
                <FlagChip label="platform" on={!!msg.is_platform} />
                <FlagChip label="automated" on={!!msg.is_automated} />
                <FlagChip label="redacted" on={!!msg.redacted} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <InfoRow label="ID" value={msg.id} />
                <InfoRow label="Hash" value={msg.hash ?? '—'} />
                <InfoRow
                  label="Sender"
                  value={
                    msg.sender_kind === 'platform' || msg.is_platform ? (
                      'FeelyFeely'
                    ) : msg.sender_uid != null && String(msg.sender_uid) !== '' ? (
                      <UserPartyLink uid={msg.sender_uid} summary={msg.sender_user_summary} />
                    ) : msg.sender_oid != null && String(msg.sender_oid) !== '' ? (
                      <OrgPartyLink oid={msg.sender_oid} summary={msg.sender_org_summary} />
                    ) : isInquiryMessage(msg) ? (
                      inquiryVisitorName(msg) || 'Visitor'
                    ) : (
                      '—'
                    )
                  }
                />
                <InfoRow
                  label="Recipient"
                  value={
                    msg.recipient_uid != null && String(msg.recipient_uid) !== '' ? (
                      <UserPartyLink uid={msg.recipient_uid} summary={msg.recipient_user_summary} />
                    ) : msg.recipient_oid != null && String(msg.recipient_oid) !== '' ? (
                      <OrgPartyLink oid={msg.recipient_oid} summary={msg.recipient_org_summary} />
                    ) : (
                      '—'
                    )
                  }
                />
                <InfoRow label="Entity" value={messageEntity(msg) ?? '—'} />
                <InfoRow label="Priority" value={msg.priority ?? 'normal'} />
                <InfoRow label="Seq" value={msg.seq ?? '—'} />
                <InfoRow label="Timestamp" value={formatEpoch(msg.timestamp ?? msg.created_at)} />
                <InfoRow
                  label="Conversation key"
                  value={
                    <span className="font-mono text-xs">
                      {msg.conversation_key ?? '(null — use party thread)'}
                    </span>
                  }
                />
              </div>

              {(canModerate || canDelete) && (
                <div>
                  <label className="mb-1 block text-xs text-gray-500">Moderation reason (optional)</label>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. spam, harassment…"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <DrawerFooter className="border-t border-gray-100">
          <div className="flex flex-wrap gap-2">
            {threadParams && onViewThread && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => onViewThread(threadParams)}
              >
                <MessagesSquare className="mr-1.5 h-4 w-4" />
                View party thread
              </Button>
            )}
            {canModerate && msg && !msg.is_deleted && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() =>
                  run('Message soft-deleted', () =>
                    softDelete.mutateAsync({
                      ref,
                      reason: reason || undefined,
                      idempotencyKey: generateIdempotencyKey(),
                    }),
                  )
                }
              >
                {softDelete.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-1.5 h-4 w-4" />
                )}
                Soft-delete
              </Button>
            )}
            {canModerate && msg?.is_deleted && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() =>
                  run('Message restored', () =>
                    restore.mutateAsync({
                      ref,
                      idempotencyKey: generateIdempotencyKey(),
                    }),
                  )
                }
              >
                {restore.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="mr-1.5 h-4 w-4" />
                )}
                Restore
              </Button>
            )}
            {canModerate && msg && !msg.redacted && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() =>
                  run('Message redacted', () =>
                    redact.mutateAsync({
                      ref,
                      reason: reason || undefined,
                      idempotencyKey: generateIdempotencyKey(),
                    }),
                  )
                }
              >
                {redact.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Eraser className="mr-1.5 h-4 w-4" />
                )}
                Redact
              </Button>
            )}
            {canModerate && msg && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() =>
                  run('Report filed', () =>
                    flag.mutateAsync({
                      body: {
                        message_ref: ref,
                        reason: reason || 'flagged by admin',
                        category: 'other',
                      },
                      idempotencyKey: generateIdempotencyKey(),
                    }),
                  )
                }
              >
                {flag.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Flag className="mr-1.5 h-4 w-4" />
                )}
                Flag
              </Button>
            )}
            {canDelete && msg && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={() => {
                  if (
                    !window.confirm(
                      'Hard-purge permanently deletes this message. This cannot be undone.',
                    )
                  )
                    return;
                  run('Message purged', () =>
                    purge.mutateAsync({
                      ref,
                      reason: reason || undefined,
                      idempotencyKey: generateIdempotencyKey(),
                    }),
                  ).then(() => {
                    onClose();
                  });
                }}
              >
                {purge.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Skull className="mr-1.5 h-4 w-4" />
                )}
                Hard purge
              </Button>
            )}
            <DrawerClose asChild>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </DrawerClose>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default MessageDetailsDrawer;
